import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Upload, FileText, Image as ImageIcon, Play, GripVertical, X,
} from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const EMPTY_PRODUCT = {
  slug: "",
  title: "",
  tagline: "",
  description: "",
  image: "",
  features: [],
  pdf: "",
  show_videos_button: false,
  category_key: "",
  overview: "",
  features_grouped: [],
  applications: [],
  specs: [],
  gallery: [],
};

const SUB_TABS = [
  { key: "basics",       label: "Basics" },
  { key: "content",      label: "Content" },
  { key: "feature-grp",  label: "Feature Groups" },
  { key: "applications", label: "Applications" },
  { key: "specs",        label: "Specs" },
  { key: "media",        label: "Media" },
  { key: "gallery",      label: "Gallery" },
  { key: "videos",       label: "Videos" },
];

const slugify = (s = "") =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** Master-detail Products Manager — Option B (sub-tabs) */
export default function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subTab, setSubTab] = useState("basics");

  // Draft inputs for chip / bullet style fields
  const [featureDraft, setFeatureDraft] = useState("");
  const [appDraft, setAppDraft] = useState("");

  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const loadProducts = async () => {
    const { data } = await api.get("/products");
    setProducts(data || []);
    return data;
  };

  useEffect(() => {
    loadProducts().then((list) => {
      if (list?.length) selectProduct(list[0]);
    });
  }, []);

  const selectProduct = (p) => {
    setIsNew(false);
    setSelectedId(p.id);
    setForm({ ...EMPTY_PRODUCT, ...p });
    setSubTab("basics");
  };

  const startNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setForm({ ...EMPTY_PRODUCT });
    setSubTab("basics");
  };

  const uploadFile = async (file, endpoint = "/admin/upload") => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file, "/admin/upload");
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Hero image uploaded.");
    } catch {
      toast.error("Image upload failed.");
    } finally {
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const onPdfFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/pdf/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      toast.error("Please choose a PDF file.");
      return;
    }
    try {
      const data = await uploadFile(file, "/admin/upload-file");
      setForm((f) => ({ ...f, pdf: data.url }));
      toast.success("Catalogue uploaded.");
    } catch {
      toast.error("PDF upload failed.");
    } finally {
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const onGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = [];
      for (const f of files) {
        const data = await uploadFile(f, "/admin/upload");
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls] }));
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} added.`);
    } catch {
      toast.error("Some uploads failed.");
    } finally {
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  // ---- Feature bullets (Products-page) ----
  const addFeature = () => {
    const v = featureDraft.trim();
    if (!v) return;
    setForm((f) => ({ ...f, features: [...(f.features || []), v] }));
    setFeatureDraft("");
  };
  const removeFeature = (idx) => setForm((f) => ({ ...f, features: f.features.filter((_, i) => i !== idx) }));

  const featureDragIndex = useRef(null);
  const onFeatureDragStart = (i) => { featureDragIndex.current = i; };
  const onFeatureDragEnter = (i) => {
    const from = featureDragIndex.current;
    if (from === null || from === i) return;
    setForm((f) => {
      const list = [...(f.features || [])];
      const [moved] = list.splice(from, 1);
      list.splice(i, 0, moved);
      return { ...f, features: list };
    });
    featureDragIndex.current = i;
  };

  // ---- Applications ----
  const addApp = () => {
    const v = appDraft.trim();
    if (!v) return;
    setForm((f) => ({ ...f, applications: [...(f.applications || []), v] }));
    setAppDraft("");
  };
  const removeApp = (idx) => setForm((f) => ({ ...f, applications: f.applications.filter((_, i) => i !== idx) }));

  // ---- Feature Groups ----
  const addGroup = () => {
    setForm((f) => ({
      ...f,
      features_grouped: [...(f.features_grouped || []), { title: "New group", items: [] }],
    }));
  };
  const removeGroup = (gi) => {
    if (!confirm("Delete this feature group?")) return;
    setForm((f) => ({
      ...f,
      features_grouped: f.features_grouped.filter((_, i) => i !== gi),
    }));
  };
  const setGroupTitle = (gi, title) => {
    setForm((f) => {
      const arr = [...f.features_grouped];
      arr[gi] = { ...arr[gi], title };
      return { ...f, features_grouped: arr };
    });
  };
  const addGroupItem = (gi, value) => {
    const v = (value || "").trim();
    if (!v) return;
    setForm((f) => {
      const arr = [...f.features_grouped];
      arr[gi] = { ...arr[gi], items: [...(arr[gi].items || []), v] };
      return { ...f, features_grouped: arr };
    });
  };
  const setGroupItem = (gi, ii, value) => {
    setForm((f) => {
      const arr = [...f.features_grouped];
      const items = [...(arr[gi].items || [])];
      items[ii] = value;
      arr[gi] = { ...arr[gi], items };
      return { ...f, features_grouped: arr };
    });
  };
  const removeGroupItem = (gi, ii) => {
    setForm((f) => {
      const arr = [...f.features_grouped];
      const items = (arr[gi].items || []).filter((_, i) => i !== ii);
      arr[gi] = { ...arr[gi], items };
      return { ...f, features_grouped: arr };
    });
  };

  // ---- Specs ----
  const addSpec = () => {
    setForm((f) => ({ ...f, specs: [...(f.specs || []), { label: "", value: "" }] }));
  };
  const setSpec = (i, key, value) => {
    setForm((f) => {
      const arr = [...f.specs];
      arr[i] = { ...arr[i], [key]: value };
      return { ...f, specs: arr };
    });
  };
  const removeSpec = (i) => {
    setForm((f) => ({ ...f, specs: f.specs.filter((_, idx) => idx !== i) }));
  };

  // ---- Gallery ----
  const removeGalleryItem = (i) =>
    setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, idx) => idx !== i) }));
  const addGalleryUrl = (url) => {
    const v = (url || "").trim();
    if (!v) return;
    setForm((f) => ({ ...f, gallery: [...(f.gallery || []), v] }));
  };

  const save = async () => {
    if (!form.slug || !form.title) {
      toast.error("Title and slug are required.");
      setSubTab("basics");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: slugify(form.slug) };
      let result;
      if (isNew) {
        result = await api.post("/products", payload);
        toast.success("Product created.");
      } else {
        result = await api.put(`/products/${selectedId}`, payload);
        toast.success("Product updated.");
      }
      await loadProducts();
      setIsNew(false);
      setSelectedId(result.data.id);
      setForm({ ...EMPTY_PRODUCT, ...result.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save product.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (!confirm("Delete this product? This cannot be undone.")) return;
    try {
      await api.delete(`/products/${selectedId}`);
      toast.success("Product deleted.");
      const list = await loadProducts();
      if (list?.length) selectProduct(list[0]);
      else { setSelectedId(null); setForm(EMPTY_PRODUCT); }
    } catch {
      toast.error("Delete failed.");
    }
  };

  const monoLabel = "font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-products-manager">
      {/* MASTER LIST */}
      <div className="lg:col-span-4">
        <div className="border border-black/10 bg-white p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8]">Product Lines</p>
              <h2 className="font-display font-semibold text-xl text-[#334155]">Manage products</h2>
            </div>
            <button
              type="button"
              onClick={startNew}
              data-testid="admin-products-add-new-btn"
              className="inline-flex items-center gap-2 bg-[#3da8d8] text-white px-4 py-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba]"
            >
              <Plus size={12} /> Add New
            </button>
          </div>
          <ul className="space-y-2">
            {products.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => selectProduct(p)}
                  data-testid={`admin-product-row-${p.slug}`}
                  className={`w-full text-left flex items-center gap-3 border p-3 transition-colors ${
                    selectedId === p.id && !isNew
                      ? "border-[#3da8d8] bg-[#eaf6fc]"
                      : "border-black/10 bg-white hover:border-[#3da8d8]/60"
                  }`}
                >
                  <div className="w-14 h-14 bg-[#eaf6fc] shrink-0 overflow-hidden flex items-center justify-center">
                    {p.image ? (
                      <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={18} className="text-black/20" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm truncate text-[#334155]">{p.title}</p>
                    <p className="text-[10px] text-[#52525B] truncate font-mono-accent">
                      {(p.pdf ? "PDF ✓" : "No PDF")} · {(p.specs?.length || 0)} specs · {(p.gallery?.length || 0)} gallery
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* DETAIL EDITOR */}
      <div className="lg:col-span-8">
        {!selectedId && !isNew ? (
          <div className="border border-dashed border-black/15 bg-white p-16 text-center text-[#52525B]">
            Select a product on the left, or click <b>+ Add New</b> to create one.
          </div>
        ) : (
          <div className="border border-black/10 bg-white" data-testid="admin-products-editor">
            <div className="px-6 py-5 border-b border-black/10 flex items-center justify-between">
              <div>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8]">
                  {isNew ? "New Product" : "Editing"}
                </p>
                <h3 className="font-display font-bold text-2xl text-[#334155]">
                  {form.title || "Untitled product"}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {!isNew && (
                  <button
                    type="button"
                    onClick={remove}
                    data-testid="admin-products-delete-btn"
                    className="inline-flex items-center gap-2 border border-red-200 text-red-600 px-4 py-2.5 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-red-50"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="admin-products-save-btn"
                  className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#1e293b] disabled:opacity-50"
                >
                  <Save size={13} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            {/* SUB-TABS */}
            <div className="border-b border-black/10 flex gap-1 overflow-x-auto px-2">
              {SUB_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setSubTab(t.key)}
                  data-testid={`admin-product-subtab-${t.key}`}
                  className={`px-4 py-3 font-mono-accent text-[10px] uppercase tracking-widest whitespace-nowrap border-b-2 -mb-px ${
                    subTab === t.key
                      ? "text-[#0F172A] border-[#3da8d8] bg-[#F5F9FC]"
                      : "text-[#52525B] border-transparent hover:text-[#334155]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-8">
              {/* ---------- BASICS ---------- */}
              {subTab === "basics" && (
                <div data-testid="admin-product-pane-basics" className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className={monoLabel}>Name *</Label>
                    <Input
                      data-testid="admin-product-name-input"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })}
                      className="mt-1 rounded-none border-black/15"
                    />
                  </div>
                  <div>
                    <Label className={monoLabel}>URL slug *</Label>
                    <Input
                      data-testid="admin-product-slug-input"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      className="mt-1 rounded-none border-black/15 font-mono-accent text-sm"
                      placeholder="modulart-in-plant-offices"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className={monoLabel}>Tagline</Label>
                    <Input
                      data-testid="admin-product-tagline-input"
                      value={form.tagline || ""}
                      onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                      className="mt-1 rounded-none border-black/15"
                      placeholder="Short strap-line shown above the product name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className={monoLabel}>Short description</Label>
                    <Textarea
                      data-testid="admin-product-description-input"
                      value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="mt-1 rounded-none border-black/15 min-h-[110px]"
                      placeholder="Two-line summary shown on the Products list & Home bento"
                    />
                  </div>
                </div>
              )}

              {/* ---------- CONTENT ---------- */}
              {subTab === "content" && (
                <div data-testid="admin-product-pane-content" className="space-y-6">
                  <div>
                    <Label className={monoLabel}>Detail-page overview (long)</Label>
                    <Textarea
                      data-testid="admin-product-overview-input"
                      value={form.overview || ""}
                      onChange={(e) => setForm({ ...form, overview: e.target.value })}
                      className="mt-1 rounded-none border-black/15 min-h-[160px]"
                      placeholder="Long-form intro shown on the product detail page under the hero image."
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={monoLabel}>Feature bullets · Products page</Label>
                      <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#94A3B8]">
                        {(form.features || []).length} bullet{(form.features || []).length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="flex gap-2 mb-3">
                      <Input
                        value={featureDraft}
                        onChange={(e) => setFeatureDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                        className="rounded-none border-black/15"
                        placeholder="Add a bullet and press Enter"
                        data-testid="admin-product-feature-input"
                      />
                      <button
                        type="button"
                        onClick={addFeature}
                        data-testid="admin-product-feature-add-btn"
                        className="bg-[#78be27] text-white px-4 hover:bg-[#6aa81f]"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {(form.features || []).map((f, idx) => (
                        <li
                          key={idx}
                          draggable
                          onDragStart={() => onFeatureDragStart(idx)}
                          onDragEnter={() => onFeatureDragEnter(idx)}
                          onDragOver={(e) => e.preventDefault()}
                          data-testid={`admin-product-feature-row-${idx}`}
                          className="flex items-center gap-2 border border-black/10 px-3 py-2 bg-[#F9FAFB] hover:border-[#3da8d8]/50"
                        >
                          <span className="cursor-grab active:cursor-grabbing text-[#94A3B8] shrink-0" title="Drag to reorder">
                            <GripVertical size={16} />
                          </span>
                          <span className="text-sm text-[#334155] flex-1">{f}</span>
                          <button
                            type="button"
                            onClick={() => removeFeature(idx)}
                            data-testid={`admin-product-feature-remove-${idx}`}
                            className="text-[#52525B] hover:text-red-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      ))}
                      {(!form.features || form.features.length === 0) && (
                        <li className="text-xs text-[#94A3B8] italic">No bullets yet.</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* ---------- FEATURE GROUPS ---------- */}
              {subTab === "feature-grp" && (
                <div data-testid="admin-product-pane-feature-grp" className="space-y-5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#52525B]">
                      Detail-page 3-column blocks. Each group has a title and a bullet list.
                    </p>
                    <button
                      type="button"
                      onClick={addGroup}
                      data-testid="admin-product-group-add-btn"
                      className="inline-flex items-center gap-2 bg-[#3da8d8] text-white px-3 py-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba]"
                    >
                      <Plus size={12} /> Add group
                    </button>
                  </div>
                  {(form.features_grouped || []).map((g, gi) => (
                    <GroupEditor
                      key={gi}
                      gi={gi}
                      group={g}
                      onTitle={(t) => setGroupTitle(gi, t)}
                      onRemove={() => removeGroup(gi)}
                      onAddItem={(v) => addGroupItem(gi, v)}
                      onEditItem={(ii, v) => setGroupItem(gi, ii, v)}
                      onRemoveItem={(ii) => removeGroupItem(gi, ii)}
                    />
                  ))}
                  {(!form.features_grouped || form.features_grouped.length === 0) && (
                    <p className="text-xs text-[#94A3B8] italic">No feature groups yet.</p>
                  )}
                </div>
              )}

              {/* ---------- APPLICATIONS ---------- */}
              {subTab === "applications" && (
                <div data-testid="admin-product-pane-applications">
                  <p className="text-sm text-[#52525B] mb-4">
                    Chips shown under &ldquo;Where it works&rdquo; on the detail page.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={appDraft}
                      onChange={(e) => setAppDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addApp(); } }}
                      className="rounded-none border-black/15"
                      placeholder="e.g. Warehouses"
                      data-testid="admin-product-app-input"
                    />
                    <button
                      type="button"
                      onClick={addApp}
                      data-testid="admin-product-app-add-btn"
                      className="bg-[#3da8d8] text-white px-4 hover:bg-[#3a82ba]"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(form.applications || []).map((a, idx) => (
                      <span
                        key={idx}
                        data-testid={`admin-product-app-row-${idx}`}
                        className="inline-flex items-center gap-2 border border-black/15 bg-white px-3 py-1.5 font-mono-accent text-[11px] uppercase tracking-widest text-[#334155]"
                      >
                        {a}
                        <button
                          type="button"
                          onClick={() => removeApp(idx)}
                          data-testid={`admin-product-app-remove-${idx}`}
                          className="text-[#94A3B8] hover:text-red-600"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    {(!form.applications || form.applications.length === 0) && (
                      <p className="text-xs text-[#94A3B8] italic">No applications yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* ---------- SPECS ---------- */}
              {subTab === "specs" && (
                <div data-testid="admin-product-pane-specs">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-[#52525B]">Label / value pairs shown in the Specifications section.</p>
                    <button
                      type="button"
                      onClick={addSpec}
                      data-testid="admin-product-spec-add-btn"
                      className="inline-flex items-center gap-2 bg-[#3da8d8] text-white px-3 py-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba]"
                    >
                      <Plus size={12} /> Add spec
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(form.specs || []).map((s, i) => (
                      <div
                        key={i}
                        data-testid={`admin-product-spec-row-${i}`}
                        className="grid grid-cols-12 gap-2 border border-black/10 bg-[#F9FAFB] p-2"
                      >
                        <Input
                          value={s.label}
                          onChange={(e) => setSpec(i, "label", e.target.value)}
                          placeholder="Label (e.g. Wall thickness)"
                          className="col-span-5 rounded-none border-black/15 text-sm"
                          data-testid={`admin-product-spec-label-${i}`}
                        />
                        <Input
                          value={s.value}
                          onChange={(e) => setSpec(i, "value", e.target.value)}
                          placeholder="Value (e.g. 112 mm)"
                          className="col-span-6 rounded-none border-black/15 text-sm"
                          data-testid={`admin-product-spec-value-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeSpec(i)}
                          data-testid={`admin-product-spec-remove-${i}`}
                          className="col-span-1 flex items-center justify-center text-[#52525B] hover:text-red-600"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                    {(!form.specs || form.specs.length === 0) && (
                      <p className="text-xs text-[#94A3B8] italic">No specs yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* ---------- MEDIA ---------- */}
              {subTab === "media" && (
                <div data-testid="admin-product-pane-media" className="grid md:grid-cols-2 gap-4">
                  <div className="border border-dashed border-black/20 p-4">
                    <p className={monoLabel}>Hero / Thumbnail image</p>
                    <p className="text-[10px] text-[#94A3B8] mb-3 normal-case leading-snug mt-1">
                      Shown on the Home page bento AND the Products page hero for this category.
                    </p>
                    {form.image ? (
                      <img src={resolveImg(form.image)} alt="" className="w-full h-32 object-cover mb-3 border border-black/10" />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center border border-dashed border-black/15 mb-3 text-[#94A3B8]">
                        <ImageIcon size={22} />
                      </div>
                    )}
                    <Input
                      value={form.image || ""}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      className="rounded-none border-black/15 text-xs mb-2"
                      placeholder="Image URL or /assets/…"
                      data-testid="admin-product-image-url"
                    />
                    <label className="inline-flex items-center gap-2 border border-black/15 px-3 py-1.5 cursor-pointer hover:border-[#3da8d8] font-mono-accent text-[10px] uppercase tracking-widest text-[#334155]">
                      <Upload size={12} /> Replace image
                      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={onHeroFile} data-testid="admin-product-image-file" />
                    </label>
                  </div>

                  <div className="border border-dashed border-black/20 p-4">
                    <p className={monoLabel}>Catalogue (PDF)</p>
                    <div className="flex items-center gap-3 mb-3 h-32 border border-black/10 bg-[#F9FAFB] px-4 mt-1">
                      <FileText size={22} className="text-[#78be27]" />
                      <div className="flex-1 min-w-0">
                        {form.pdf ? (
                          <>
                            <p className="text-sm font-mono-accent truncate">{form.pdf.split("/").pop()}</p>
                            <a href={resolveImg(form.pdf)} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#3da8d8] hover:underline">
                              Preview →
                            </a>
                          </>
                        ) : (
                          <p className="text-sm text-[#94A3B8] italic">No catalogue attached yet</p>
                        )}
                      </div>
                    </div>
                    <Input
                      value={form.pdf || ""}
                      onChange={(e) => setForm({ ...form, pdf: e.target.value })}
                      className="rounded-none border-black/15 text-xs mb-2"
                      placeholder="PDF URL or /assets/…"
                      data-testid="admin-product-pdf-url"
                    />
                    <label className="inline-flex items-center gap-2 border border-black/15 px-3 py-1.5 cursor-pointer hover:border-[#78be27] font-mono-accent text-[10px] uppercase tracking-widest text-[#334155]">
                      <Upload size={12} /> Replace catalogue
                      <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden" onChange={onPdfFile} data-testid="admin-product-pdf-file" />
                    </label>
                  </div>
                </div>
              )}

              {/* ---------- GALLERY ---------- */}
              {subTab === "gallery" && (
                <div data-testid="admin-product-pane-gallery">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-[#52525B]">Thumbnails shown in the detail page &ldquo;Applications&rdquo; column.</p>
                    <label className="inline-flex items-center gap-2 bg-[#3da8d8] text-white px-3 py-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba] cursor-pointer">
                      <Upload size={12} /> Upload images
                      <input
                        ref={galleryInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={onGalleryFiles}
                        className="hidden"
                        data-testid="admin-product-gallery-file"
                      />
                    </label>
                  </div>
                  <GalleryUrlAdd onAdd={addGalleryUrl} />
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {(form.gallery || []).map((g, i) => (
                      <div key={i} className="relative group aspect-square border border-black/10 overflow-hidden" data-testid={`admin-product-gallery-item-${i}`}>
                        <img src={resolveImg(g)} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeGalleryItem(i)}
                          data-testid={`admin-product-gallery-remove-${i}`}
                          className="absolute top-1 right-1 bg-white/95 text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    {(!form.gallery || form.gallery.length === 0) && (
                      <p className="text-xs text-[#94A3B8] italic col-span-3">No gallery images yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* ---------- VIDEOS ---------- */}
              {subTab === "videos" && (
                <div data-testid="admin-product-pane-videos">
                  <div className="border border-dashed border-black/15 bg-[#F9FAFB] p-5 mb-4 flex items-start gap-3">
                    <Play size={18} className="text-[#3da8d8] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#52525B] leading-relaxed">
                      Video upload &amp; management will unlock once you confirm the storage method
                      (YouTube/Vimeo URL, direct MP4 upload, or both). Meanwhile use the toggle below to
                      decide whether a &ldquo;Videos&rdquo; button should appear under this product on the Products page.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between border border-black/10 px-4 py-3 bg-white">
                      <div>
                        <p className="font-display text-sm text-[#334155]">Show &ldquo;Videos&rdquo; button</p>
                        <p className="text-[10px] text-[#52525B]">Adds a button under this product on /products</p>
                      </div>
                      <Switch
                        checked={!!form.show_videos_button}
                        onCheckedChange={(v) => setForm({ ...form, show_videos_button: v })}
                        data-testid="admin-product-videos-toggle"
                      />
                    </div>
                    <div>
                      <Label className={monoLabel}>Videos tab key</Label>
                      <Input
                        value={form.category_key || ""}
                        onChange={(e) => setForm({ ...form, category_key: e.target.value })}
                        className="mt-1 rounded-none border-black/15 font-mono-accent text-sm"
                        placeholder='e.g. "in-plant" or "partition"'
                        data-testid="admin-product-category-key"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Single feature group editor with inline item list */
function GroupEditor({ gi, group, onTitle, onRemove, onAddItem, onEditItem, onRemoveItem }) {
  const [itemDraft, setItemDraft] = useState("");
  return (
    <div className="border border-black/10 bg-[#F9FAFB] p-4" data-testid={`admin-product-group-${gi}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#3da8d8] shrink-0">Group {gi + 1}</span>
        <Input
          value={group.title || ""}
          onChange={(e) => onTitle(e.target.value)}
          placeholder="Group title"
          className="rounded-none border-black/15 flex-1"
          data-testid={`admin-product-group-title-${gi}`}
        />
        <button
          type="button"
          onClick={onRemove}
          data-testid={`admin-product-group-remove-${gi}`}
          className="text-[#52525B] hover:text-red-600 shrink-0"
          title="Delete group"
        >
          <Trash2 size={15} />
        </button>
      </div>
      <ul className="space-y-2 mb-3">
        {(group.items || []).map((it, ii) => (
          <li key={ii} className="flex items-center gap-2" data-testid={`admin-product-group-${gi}-item-${ii}`}>
            <Input
              value={it}
              onChange={(e) => onEditItem(ii, e.target.value)}
              className="rounded-none border-black/15 text-sm"
              data-testid={`admin-product-group-${gi}-item-input-${ii}`}
            />
            <button
              type="button"
              onClick={() => onRemoveItem(ii)}
              data-testid={`admin-product-group-${gi}-item-remove-${ii}`}
              className="text-[#52525B] hover:text-red-600 shrink-0"
            >
              <Trash2 size={14} />
            </button>
          </li>
        ))}
        {(!group.items || group.items.length === 0) && (
          <li className="text-xs text-[#94A3B8] italic">No bullets in this group yet.</li>
        )}
      </ul>
      <div className="flex gap-2">
        <Input
          value={itemDraft}
          onChange={(e) => setItemDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (itemDraft.trim()) { onAddItem(itemDraft); setItemDraft(""); }
            }
          }}
          placeholder="Add a bullet and press Enter"
          className="rounded-none border-black/15 text-sm"
          data-testid={`admin-product-group-${gi}-item-input`}
        />
        <button
          type="button"
          onClick={() => {
            if (itemDraft.trim()) { onAddItem(itemDraft); setItemDraft(""); }
          }}
          data-testid={`admin-product-group-${gi}-item-add`}
          className="bg-[#78be27] text-white px-3 hover:bg-[#6aa81f]"
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

/** Small helper input to add an image by URL to the gallery */
function GalleryUrlAdd({ onAdd }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2">
      <Input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="Or paste an image URL / /assets/…"
        className="rounded-none border-black/15 text-sm"
        data-testid="admin-product-gallery-url-input"
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); if (v.trim()) { onAdd(v); setV(""); } }
        }}
      />
      <button
        type="button"
        onClick={() => { if (v.trim()) { onAdd(v); setV(""); } }}
        data-testid="admin-product-gallery-url-add"
        className="bg-[#3da8d8] text-white px-4 hover:bg-[#3a82ba]"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}
